/**
 * Activity Log Service
 *
 * PE-769: Tracks and logs all changes to records for activity timeline display.
 * Automatically logs: creation, updates, status changes, relationships, subtasks.
 */

import { createPersistedMockData } from './mockDataManager';

export type ActivityAction =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'status_changed'
  | 'assigned'
  | 'related_added'
  | 'related_removed'
  | 'subtask_created'
  | 'subtask_completed'
  | 'subtask_reopened'
  | 'comment_added'
  | 'note_added';

export interface FieldChange {
  fieldName: string;
  fieldLabel: string;
  oldValue: any;
  newValue: any;
}

export interface ActivityEntry {
  id: string;
  recordId: string;
  objectType: string;
  action: ActivityAction;

  // Field changes (for updates)
  fieldChanges?: FieldChange[];

  // Related record info (for related_added/removed)
  relatedRecordId?: string;
  relatedObjectType?: string;
  relatedRecordName?: string;

  // Status change info
  oldStatus?: string;
  newStatus?: string;

  // User info
  userId: string;
  userName: string;

  // Metadata
  timestamp: string;
  metadata?: Record<string, any>; // For extensibility
}

// Default activity entries (empty - will be populated by logging)
const defaultActivityLog: ActivityEntry[] = [];

// Storage using persistence pattern
export const persistedActivityLog = createPersistedMockData<ActivityEntry>(
  'crm_activity_log',
  defaultActivityLog
);

/**
 * Get all activity for a specific record
 */
export const getActivityForRecord = (
  recordId: string,
  objectType?: string
): ActivityEntry[] => {
  const allActivity = persistedActivityLog.get();

  let filtered = allActivity.filter(entry => entry.recordId === recordId);

  if (objectType) {
    filtered = filtered.filter(entry => entry.objectType === objectType);
  }

  // Sort by timestamp descending (newest first)
  return filtered.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
};

/**
 * Get recent activity across all records (for dashboard)
 */
export const getRecentActivity = (limit: number = 50): ActivityEntry[] => {
  const allActivity = persistedActivityLog.get();

  return allActivity
    .sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )
    .slice(0, limit);
};

/**
 * Log a record creation
 */
export const logRecordCreated = (
  recordId: string,
  objectType: string,
  userId: string,
  userName: string,
  metadata?: Record<string, any>
): void => {
  const entry: ActivityEntry = {
    id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId,
    objectType,
    action: 'created',
    userId,
    userName,
    timestamp: new Date().toISOString(),
    metadata,
  };

  const allActivity = persistedActivityLog.get();
  persistedActivityLog.set([...allActivity, entry]);
};

/**
 * Log a record update with field changes
 */
export const logRecordUpdated = (
  recordId: string,
  objectType: string,
  fieldChanges: FieldChange[],
  userId: string,
  userName: string
): void => {
  // Only log if there are actual changes
  if (fieldChanges.length === 0) return;

  const entry: ActivityEntry = {
    id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId,
    objectType,
    action: 'updated',
    fieldChanges,
    userId,
    userName,
    timestamp: new Date().toISOString(),
  };

  const allActivity = persistedActivityLog.get();
  persistedActivityLog.set([...allActivity, entry]);
};

/**
 * Log a status change
 */
export const logStatusChanged = (
  recordId: string,
  objectType: string,
  oldStatus: string,
  newStatus: string,
  userId: string,
  userName: string
): void => {
  if (oldStatus === newStatus) return;

  const entry: ActivityEntry = {
    id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId,
    objectType,
    action: 'status_changed',
    oldStatus,
    newStatus,
    userId,
    userName,
    timestamp: new Date().toISOString(),
  };

  const allActivity = persistedActivityLog.get();
  persistedActivityLog.set([...allActivity, entry]);
};

/**
 * Log assignment change
 */
export const logAssignmentChanged = (
  recordId: string,
  objectType: string,
  oldAssignee: string,
  newAssignee: string,
  userId: string,
  userName: string
): void => {
  if (oldAssignee === newAssignee) return;

  const entry: ActivityEntry = {
    id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId,
    objectType,
    action: 'assigned',
    fieldChanges: [
      {
        fieldName: 'assignedTo',
        fieldLabel: 'Assigned To',
        oldValue: oldAssignee,
        newValue: newAssignee,
      },
    ],
    userId,
    userName,
    timestamp: new Date().toISOString(),
  };

  const allActivity = persistedActivityLog.get();
  persistedActivityLog.set([...allActivity, entry]);
};

/**
 * Log related record added
 */
export const logRelatedRecordAdded = (
  recordId: string,
  objectType: string,
  relatedRecordId: string,
  relatedObjectType: string,
  relatedRecordName: string,
  userId: string,
  userName: string
): void => {
  const entry: ActivityEntry = {
    id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId,
    objectType,
    action: 'related_added',
    relatedRecordId,
    relatedObjectType,
    relatedRecordName,
    userId,
    userName,
    timestamp: new Date().toISOString(),
  };

  const allActivity = persistedActivityLog.get();
  persistedActivityLog.set([...allActivity, entry]);
};

/**
 * Log related record removed
 */
export const logRelatedRecordRemoved = (
  recordId: string,
  objectType: string,
  relatedRecordId: string,
  relatedObjectType: string,
  relatedRecordName: string,
  userId: string,
  userName: string
): void => {
  const entry: ActivityEntry = {
    id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId,
    objectType,
    action: 'related_removed',
    relatedRecordId,
    relatedObjectType,
    relatedRecordName,
    userId,
    userName,
    timestamp: new Date().toISOString(),
  };

  const allActivity = persistedActivityLog.get();
  persistedActivityLog.set([...allActivity, entry]);
};

/**
 * Log subtask created
 */
export const logSubtaskCreated = (
  parentTaskId: string,
  subtaskTitle: string,
  userId: string,
  userName: string
): void => {
  const entry: ActivityEntry = {
    id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId: parentTaskId,
    objectType: 'Task__c',
    action: 'subtask_created',
    metadata: { subtaskTitle },
    userId,
    userName,
    timestamp: new Date().toISOString(),
  };

  const allActivity = persistedActivityLog.get();
  persistedActivityLog.set([...allActivity, entry]);
};

/**
 * Log subtask completed
 */
export const logSubtaskCompleted = (
  parentTaskId: string,
  subtaskTitle: string,
  userId: string,
  userName: string
): void => {
  const entry: ActivityEntry = {
    id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId: parentTaskId,
    objectType: 'Task__c',
    action: 'subtask_completed',
    metadata: { subtaskTitle },
    userId,
    userName,
    timestamp: new Date().toISOString(),
  };

  const allActivity = persistedActivityLog.get();
  persistedActivityLog.set([...allActivity, entry]);
};

/**
 * Log subtask reopened
 */
export const logSubtaskReopened = (
  parentTaskId: string,
  subtaskTitle: string,
  userId: string,
  userName: string
): void => {
  const entry: ActivityEntry = {
    id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId: parentTaskId,
    objectType: 'Task__c',
    action: 'subtask_reopened',
    metadata: { subtaskTitle },
    userId,
    userName,
    timestamp: new Date().toISOString(),
  };

  const allActivity = persistedActivityLog.get();
  persistedActivityLog.set([...allActivity, entry]);
};

/**
 * Log note added
 */
export const logNoteAdded = (
  recordId: string,
  objectType: string,
  userId: string,
  userName: string,
  notePreview?: string
): void => {
  const entry: ActivityEntry = {
    id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    recordId,
    objectType,
    action: 'note_added',
    metadata: { notePreview: notePreview?.slice(0, 100) },
    userId,
    userName,
    timestamp: new Date().toISOString(),
  };

  const allActivity = persistedActivityLog.get();
  persistedActivityLog.set([...allActivity, entry]);
};

/**
 * Get all activity (for debugging)
 */
export const getAllActivity = (): ActivityEntry[] => {
  return persistedActivityLog.get();
};

/**
 * Clear activity for a record (when record deleted)
 */
export const clearActivityForRecord = (recordId: string): void => {
  const allActivity = persistedActivityLog.get();
  const filtered = allActivity.filter(entry => entry.recordId !== recordId);
  persistedActivityLog.set(filtered);
};

/**
 * Helper: Detect changes between old and new data
 */
export const detectFieldChanges = (
  oldData: Record<string, any>,
  newData: Record<string, any>,
  fieldLabels?: Record<string, string>
): FieldChange[] => {
  const changes: FieldChange[] = [];

  // Fields to skip (internal/metadata)
  const skipFields = ['id', 'createdDate', 'lastModified', 'lastModifiedDate', 'notes'];

  Object.keys(newData).forEach(key => {
    if (skipFields.includes(key)) return;

    const oldValue = oldData[key];
    const newValue = newData[key];

    // Skip if no change
    if (oldValue === newValue) return;
    if (oldValue === '' && newValue === '') return;
    if (oldValue == null && newValue === '') return;
    if (oldValue === '' && newValue == null) return;

    // Convert field name to label (camelCase to Title Case)
    const fieldLabel =
      fieldLabels?.[key] ||
      key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();

    changes.push({
      fieldName: key,
      fieldLabel,
      oldValue: oldValue ?? '',
      newValue: newValue ?? '',
    });
  });

  return changes;
};

/**
 * Get activity count for a record
 */
export const getActivityCount = (recordId: string): number => {
  const allActivity = persistedActivityLog.get();
  return allActivity.filter(entry => entry.recordId === recordId).length;
};
